<template>
  <!--
    O `@click` aqui é DELEGAÇÃO, e é o que permite o botão da célula existir sem
    `onclick` inline. O botão é renderizado por um `cellRenderer` que devolve
    string HTML, e HTML injetado por `v-html` não carrega listener — daí a
    tentação do `onclick="..."`, que era o que este arquivo fazia.

    O clique borbulha até esta raiz; `closest()` identifica o botão e o
    `data-cliente-id` diz qual linha. Some com o global
    `window.__waOpenConversation` e, principalmente, deixa a saída do renderer
    SANITIZÁVEL: todo sanitizador remove atributo `on*`, então enquanto o
    `onclick` existisse era impossível limpar o v-html das células (task #121).
  -->
  <div class="tab-panel" @click="handleDelegatedClick">
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="{ show: true, text: t('Atualizar') }"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useDeviceSelector } from '../../composables/useDeviceSelector';

type Id = number | string;

interface ClienteRow {
  cliente_id: Id;
  cliente_nome?: string;
  cliente_telefone?: string;
  cliente_ddd?: string;
  cliente_ddi?: string;
  cliente_franquia_id?: Id;
  franquia?: string;
  whatsapp_device?: string;
  [key: string]: unknown;
}

interface DataTableInstance {
  loadData: () => void;
  getFilteredData?: () => ClienteRow[];
}

interface CellParams {
  data: ClienteRow & { _isFrozenFooter?: boolean };
  value?: unknown;
}

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();
const dataTableRef = ref<DataTableInstance | null>(null);
const { resolveDevice, buildPhone } = useDeviceSelector();

const endpoint = '/index.php/gestao_clientes/buscaClientesListaContatosFidelizacao';

const tableConfig = computed(() => ({
  tableId: 'waBottomContatosPendentes',
  title: '',
  columns: tableColumns.value,
  pagination: true,
  pageLength: 50,
  heightMode: 'fixed',
  height: '300',
}));

const filterConfig = computed(() => ({
  columns: {
    franquia: 'select',
    cliente_nome: 'text',
    localidade: 'text',
  }
}));

const tableColumns = computed(() => [
  {
    data: 'cliente_id',
    title: t('ID'),
    width: 70,
    resizable: true,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (value: Id | undefined) => { if (value) eventBus.emit('cliente.edit', value); },
    dataType: 'number',
  },
  {
    data: 'franquia',
    title: t('Franquia'),
    width: 130,
    resizable: true,
    sortable: true,
  },
  {
    data: 'cliente_nome',
    title: t('Nome'),
    width: 200,
    resizable: true,
    sortable: true,
  },
  {
    data: 'cliente_data_ultimo_compromisso',
    title: t('Último compromisso'),
    width: 160,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date',
  },
  {
    data: 'cliente_data_ultimo_contato_fidelizacao',
    title: t('Último contato'),
    width: 160,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date',
  },
  {
    data: 'whatsapp_device',
    title: t('Dispositivo WhatsApp'),
    width: 180,
    resizable: true,
    sortable: true,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      if (params.data._isFrozenFooter) return '';
      const device = params.data.whatsapp_device;
      if (!device) {
        return `<span style="color: var(--app-text-secondary-color); font-size: 12px;">—</span>`;
      }
      return `<span style="display:inline-flex;align-items:center;gap:5px;font-size:12px;"><i class="pi pi-whatsapp" style="color:var(--app-success-color)"></i>${device}</span>`;
    }
  },
  {
    data: 'cliente_id',
    title: t('WhatsApp'),
    width: 150,
    sortable: false,
    cellRenderer: (params: CellParams): string => {
      if (params.data._isFrozenFooter) return '';
      const id = params.data.cliente_id;
      // `data-cliente-id` em vez de `onclick`: quem escuta é a delegação na
      // raiz do componente. `data-*` sobrevive a qualquer sanitizador; `on*`
      // não sobrevive a nenhum.
      return `<button data-testid="messaging-inbox-open-conversation-btn" data-cliente-id="${escaparAtributo(id)}" class="p-button p-button-success p-button-sm"><i class="pi pi-comments"></i> ${t('Abrir conversa')}</button>`;
    },
  },
]);

const loadData = (): void => {
  log('MessagingBottomBarContatosPendentes', 'loadData', 'Loading');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  emit('update:total', data.length);
};

const handleDataError = (err: unknown): void => {
  log('MessagingBottomBarContatosPendentes', 'handleDataError', 'Error', err, 'error');
};

const handleEditClick = (value: Id | undefined): void => {
  if (value) eventBus.emit('cliente.edit', value);
};

const openConversation = (client: ClienteRow): void => {
  const phone = buildPhone(client);

  if (!phone) {
    eventBus.emit('globalMessage.warning', t('Cliente sem telefone cadastrado'));
    return;
  }

  const { deviceId, needsPicker, candidates } = resolveDevice(client);

  if (needsPicker) {
    eventBus.emit('modal.whatsapp-device-picker.open', {
      candidates,
      onSelect: (selectedDeviceId: Id) => {
        eventBus.emit('whatsapp.select-conversation', { phone, deviceId: selectedDeviceId });
      },
    });
    return;
  }

  eventBus.emit('whatsapp.select-conversation', { phone, deviceId: deviceId ?? null });
  log('MessagingBottomBarContatosPendentes', 'openConversation', 'selectConversation', { phone, deviceId });
};

const handleClienteSaved = (): void => loadData();

/**
 * Escapa o valor antes de ele virar atributo HTML.
 *
 * `cliente_id` é numérico hoje, mas o tipo é `number | string` e o valor vem do
 * banco. Interpolar direto num atributo entre aspas é a forma clássica de sair
 * do atributo — e aqui a string vira HTML de verdade, via `v-html`.
 */
const escaparAtributo = (valor: Id): string =>
  String(valor).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/**
 * Delegação: um listener na raiz, em vez de um `onclick` por linha.
 *
 * O botão nasce dentro de um `cellRenderer` que devolve string, e string
 * injetada por `v-html` não carrega listener. A saída anterior era um
 * `onclick="window.__waOpenConversation(id)"` mais um global — que além de
 * vazar `window`, tornava a saída do renderer IMPOSSÍVEL de sanitizar, porque
 * todo sanitizador remove atributo `on*`.
 */
const handleDelegatedClick = (evento: MouseEvent): void => {
  const alvo = (evento.target as HTMLElement | null)?.closest?.(
    '[data-testid="messaging-inbox-open-conversation-btn"]',
  ) as HTMLElement | null;

  if (!alvo) return;

  const clienteId = alvo.dataset.clienteId;
  if (clienteId === undefined) return;

  const allData = dataTableRef.value?.getFilteredData?.() || [];
  // `==` de propósito: o `data-*` volta SEMPRE como string, e `cliente_id` na
  // linha pode ser number. Era assim antes e continua sendo o comportamento
  // correto aqui.
  const row = allData.find((r) => r.cliente_id == clienteId);
  if (row) openConversation(row);
};

onMounted(() => {
  eventBus.on('cliente.saved', handleClienteSaved);
});

onBeforeUnmount(() => {
  eventBus.off('cliente.saved', handleClienteSaved);
});

defineExpose({ loadData });
</script>

<style scoped>
.tab-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
